import { getTruckSpec, categoryAxles, formatNo, supportsTrailer, BAG_TONS } from './truckCatalog';

const DEFAULT_AXLES = 3;

// Tippis bilforslag. Sekker løftes av, så de gir kranbil (aldri med henger). Ellers tippbil med færrest aksler
// som tar hele mengden på ett lass; er mengden større enn alle, foreslås største bil (flere lass). Tippi foreslår aldri henger.
export function suggestTruck({ unit, quantityTons } = {}, overrides = {}) {
  if (unit === 'bags') return { category: 'crane', axles: DEFAULT_AXLES };
  const tons = Number(quantityTons);
  if (quantityTons === '' || quantityTons == null || !Number.isFinite(tons) || tons <= 0) {
    return { category: 'tipper', axles: DEFAULT_AXLES };
  }
  const options = categoryAxles('tipper');
  const fit = options.find(a => getTruckSpec('tipper', a, overrides).payloadTons >= tons);
  if (fit != null) return { category: 'tipper', axles: fit };
  return { category: 'tipper', axles: options[options.length - 1] };
}

const round1 = n => Math.round(n * 10) / 10;

// Samlet nyttelast i tonn for bilvalget (bil, pluss henger når valgt og støttet). null ved ukjent bil.
export function truckPayload(truck, overrides = {}) {
  const spec = getTruckSpec(truck?.category, truck?.axles, overrides);
  if (!spec) return null;
  if (truck.trailer === true && supportsTrailer(truck.category)) {
    return round1(spec.payloadTons + getTruckSpec('trailer', null, overrides).payloadTons);
  }
  return spec.payloadTons;
}

// Fordeler tonn per lass på bil og henger etter nyttelast. Summen er alltid lik mengden (avrundet til 0,1).
export function splitLoad(tonsPerLoad, truckPayloadTons, trailerPayloadTons) {
  const t = Number(tonsPerLoad), a = Number(truckPayloadTons), b = Number(trailerPayloadTons);
  if (![t, a, b].every(Number.isFinite) || t <= 0 || a <= 0 || b <= 0) return null;
  let truck = round1(t * a / (a + b));
  if (t >= 0.2) truck = Math.min(Math.max(truck, 0.1), round1(t - 0.1));
  return { truck, trailer: round1(t - truck) };
}

const parseNo = s => Number(String(s ?? '').trim().replace(',', '.'));
const fmtInput = n => formatNo(String(round1(n)));

// Bestiller endrer ett felt (field = 'truck' | 'trailer'); det andre feltet settes til resten. Verdiene er tekst.
export function rebalanceSplit(field, text, tonsPerLoad) {
  const n = parseNo(text);
  const rest = text !== '' && Number.isFinite(n) ? fmtInput(Number(tonsPerLoad) - n) : '';
  return field === 'truck' ? { truck: text, trailer: rest } : { truck: rest, trailer: text };
}

// Feilmelding eller null. split: { truck, trailer } som tall.
export function validateSplit(split, tonsPerLoad) {
  const a = Number(split?.truck), b = Number(split?.trailer);
  if (!(a > 0) || !(b > 0)) return 'Bil og henger må hver ha over 0 tonn.';
  if (Math.abs(round1(a) + round1(b) - Number(tonsPerLoad)) >= 0.01) return `Bil og henger må til sammen være ${formatNo(tonsPerLoad)} tonn.`;
  return null;
}

// Antall lass og tonn per lass. null ved ugyldig mengde eller nyttelast.
export function calcLoads(quantityTons, payloadTons) {
  if (quantityTons === '' || quantityTons == null) return null;
  const q = Number(quantityTons);
  const p = Number(payloadTons);
  if (!Number.isFinite(q) || !Number.isFinite(p) || q <= 0 || p <= 0) return null;
  const loads = Math.max(1, Math.ceil(q / p - 1e-9));
  return { loads, tonsPerLoad: Math.max(0.1, Math.round((q / loads) * 10) / 10) };
}

// Hvor mange storsekker en bil med gitt nyttelast (tonn) tar, og sekker per lass for et lass på gitt tonn.
export const bagsOnTruck = payloadTons => Math.floor(Number(payloadTons) / BAG_TONS + 1e-9);
export const bagsPerLoad = tonsPerLoad => Math.max(1, Math.ceil(Number(tonsPerLoad) / BAG_TONS - 1e-9));

// «2 lass à 10 t» (tonn) eller «2 lass à 10 sekker» (sekker). null ved ett lass eller manglende data.
export function loadsText(info, unit = 'tons') {
  if (!info || !Number.isFinite(info.loads) || info.loads <= 1) return null;
  return unit === 'bags'
    ? `${info.loads} lass à ${bagsPerLoad(info.tonsPerLoad)} sekker`
    : `${info.loads} lass à ${formatNo(info.tonsPerLoad)} t`;
}

// Advarsel (blokkerer ikke) når valgt bil ikke passer til lasten.
export function truckWarning({ category, unit }) {
  if (unit === 'bags' && category !== 'crane' && category !== 'crane_grab') return 'Sekker må løftes av. Velg kranbil.';
  return null;
}
